"use client"

import clsx from "clsx"
import type { z } from "zod"

import { Field, FieldPhone } from "@unlikelystudio/react-ecommerce-hooks"

import type { PropsWithClassName } from "~/@types/generics"
import { ALERT_CODES } from "~/lib/alert"
import { useAlerts } from "~/hooks/useAlerts"
import useContactFormMutation from "~/hooks/useContactFormMutation"
import { useFormWithMutation } from "~/hooks/useFormWithMutation"
import Checkbox from "~/components/ui/Checkbox"
import RichText from "~/components/ui/RichText"
import { globalCheckboxPreset } from "~/components/abstracts/Checkbox/utils/global-checkbox-preset"
import LayoutForm from "~/components/abstracts/LayoutForm"
import Select from "~/components/abstracts/Select"
import type { contactFormMutationSchema } from "~/components/forms/ContactForm/_data/schema"
import { FORM_NAMES, FormTemplate } from "~/components/forms/FormTemplate"
import { useTranslate } from "~/providers/I18nProvider/hooks/useTranslate"
import type { TContactContent } from "~/app/[locale]/contact/_components/ContactContent/_data/serializer"

import { sprinkles } from "~/styles/sprinkles.css"

import * as css from "./styles.css"

export type ContactFormMutationProps = z.infer<typeof contactFormMutationSchema>

type ContactFormProps = PropsWithClassName<Pick<NonNullable<TContactContent>, "formSelect" | "legals">>

function ContactForm({ className, formSelect, legals }: ContactFormProps) {
  const options =
    formSelect && formSelect.flatMap((item) => (item.text ? { value: item?.text, label: item?.text } : []))
  const triggerAlert = useAlerts()
  const t = useTranslate()
  const { mutate: contactFormMutation, isLoading } = useContactFormMutation({
    onSuccess: () => {
      triggerAlert(ALERT_CODES.FORM_SUCCESS)
      form.reset()
    },
    onError: () => {
      triggerAlert(ALERT_CODES.SERVER_ERROR)
    },
  })

  const { form, onSubmit } = useFormWithMutation<ContactFormMutationProps>(
    contactFormMutation,
    {
      defaultValues: {
        phoneCountry: "FR",
      },
    },
    "contact"
  )

  return (
    <div className={clsx(css.ContactForm, className)}>
      <FormTemplate formName={FORM_NAMES.CONTACT}>
        <LayoutForm form={form} onSubmit={onSubmit}>
          <Field
            label={t("form_first_name")}
            name="firstName"
            type="text"
            labelRequiredText=" *"
            registerOptions={{
              required: {
                value: true,
                message: t("form_required_first_name"),
              },
            }}
          />
          <Field
            label={t("form_last_name")}
            name="lastName"
            type="text"
            labelRequiredText=" *"
            registerOptions={{
              required: {
                value: true,
                message: t("form_required_last_name"),
              },
            }}
          />
          <Field
            label={t("form_email")}
            name="email"
            type="email"
            labelRequiredText=" *"
            registerOptions={{
              required: {
                value: true,
                message: t("form_required_email"),
              },
            }}
          />
          <FieldPhone
            registerOptions={{
              required: {
                value: true,
                message: t("form_required_phone"),
              },
            }}
          />
          {options && <Select name="subject" label={t("form_contact_subject")} options={options} />}
          <Field label={t("form_contact_order_number")} name="orderNumber" type="text" labelRequiredText=" *" />
          <Field label={t("form_contact_message")} name="message" type="textarea" isTextarea />
          <Checkbox
            checkmarkClassName={sprinkles({
              borderColor: "grey-alto",
            })}
            checkmarkActiveClassName={sprinkles({
              borderColor: "black",
            })}
            errorClassname={css.legalsInputError}
            preset={globalCheckboxPreset.acceptTerms}
          >
            <RichText
              className={clsx(css.legalsLabel, sprinkles({ fontSize: 12, lineHeight: 1.2 }))}
              disableStyles
              render={legals}
            />
          </Checkbox>
          <Field
            type="submit"
            name="submit"
            inputProps={{
              disabled: isLoading,
            }}
          />
        </LayoutForm>
      </FormTemplate>
    </div>
  )
}

export { ContactForm }
